<script lang="ts">
    import fsm from 'svelte-fsm';
    import SvgIcon from '$lib/components/assets/SvgIcon.svelte';
    import {emphLearn} from '$lib/utils/emph-learn';
    import type {WordId} from '$lib/classes/data/_primitives';
    import {Status, type UserWord} from '$lib/classes/data/user-word';
    import {getContext} from 'svelte';
    import {keyUserActionProcessor} from '$lib/context-keys';
    import type {UserActionProcessor} from '$lib/classes/services/user-action-processor';

    export let id: WordId
    export let userWord: UserWord | undefined

    const userActionProcessor: UserActionProcessor = getContext(keyUserActionProcessor)

    $: status = userWord?.status.toString()

    function handleClick(btnStatus: Status) {
        userActionProcessor.saveWord(id, btnStatus)
        if (btnStatus === 'LEARN') emphLearn()
    }

    let removeBtnTooltip = 'Remove?'
    let confirmRemove = false
    // Remove Btn Routine
    const removeBtn = fsm('idle', {
        idle: {
            click() {
                removeBtnTooltip = 'Are you sure?'
                confirmRemove = true
                return 'confirm'
            }
        },
        confirm: {
            click() {
                userActionProcessor.saveWord(id, null)
                return 'idle'
            },
            cancelRemove() {
                return 'idle'
            },
            _exit() {
                removeBtnTooltip = 'Remove?'
                confirmRemove = false
            }
        }
    });

</script>

<div id="known-learn-remove">

    <button id="known-btn" class="tooltip"
            data-testid="save-as-known-btn"
            class:status={status === 'KNOWN'}
            aria-label="mark as known"
            on:click={() => handleClick(Status.Known)}
            data-tooltip={status === 'KNOWN' ? "Marked as\n'KNOWN'" : "Know this?"}>

        <SvgIcon name="known" width="32px" height="32px"
                 viewBox="0 0 32 32"
                 fill="{status === 'KNOWN' ? 'var(--text-on-color)' : status === 'LEARN' ? `var(--color-accent)` : 'currentcolor'}"
        />
    </button>

    <button id="learn-btn" class="tooltip"
            data-testid="save-to-learn-btn"
            class:status={status === 'LEARN'}
            aria-label="mark to learn"
            on:click={() => handleClick(Status.Learn)}
            data-tooltip={status === 'LEARN' ? "Learning ✓" : "Learn this?"}>

        <SvgIcon name="learn" width="32px" height="32px"
                 viewBox="-2 -2 36 36"
                 fill="{status === 'LEARN' ? 'var(--text-on-color)' : status === 'KNOWN' ? `var(--color-accent)` : 'currentcolor'}"
        />
    </button>

    <button id="remove-btn" class="tooltip"
            data-testid="remove-word-btn"
            aria-label="remove word"
            class:invisible={!status}
            style:background={confirmRemove ? 'red' : ''}
            on:click={removeBtn.click}
            on:mouseleave={removeBtn.cancelRemove}
            data-tooltip={removeBtnTooltip}>
        <span>
        <SvgIcon name="remove" width="32px" height="32px"
                 viewBox="-6 -6 44 44"
                 fill={`var(--color-accent)`}
                 style={confirmRemove ? 'fill: var(--color-main);' : ''}/>
        </span>
    </button>

</div>

<style>

    #known-learn-remove {
        margin-left: auto;
        display: flex;
        flex-direction: column;
    }

    button {
        margin: 5px 0;
        height: 2.4rem;
        width: 2.4rem;
        padding: .2rem;
        background: var(--color-light);
        transition: var(--transition);
        transition-property: background, box-shadow;
    }

    button:focus {
        box-shadow: none;
    }

    button:hover {
        box-shadow: 0 0 0 2px var(--color-accent) inset;
        fill: var(--color-accent);
    }

    .status {
        background: var(--color-accent);
        fill: white;
    }

    #remove-btn span {
        opacity: .6;
    }

    #remove-btn:hover span {
        opacity: 1;
    }

    #remove-btn:hover {
        background: var(--color-light);
        box-shadow: revert;
    }

    #remove-btn:hover :global(svg) {
        fill: var(--color-main);
    }

    .tooltip {
        position: relative;
    }

    .tooltip::before {
        content: attr(data-tooltip);
        position: absolute;
        white-space: nowrap;
        background: var(--color-accent);
        color: white;
        font-size: .8rem;
        font-weight: bold;
        padding: .35rem .5rem;
        border-radius: .2rem;
        top: 50%;
        left: 0;
        transform: translate(calc(-100% - .4em), -50%);
        opacity: 0;
        transition: opacity 400ms;
        pointer-events: none;
    }

    #known-btn.tooltip::before {
        white-space: pre;
    }

    .tooltip:hover::before {
        opacity: 100;
    }

</style>